import {
  createFileRoute,
  type ErrorComponentProps,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { ChevronDown, ChevronRight, WifiOff } from "lucide-react";
import { Fragment, useState } from "react";

import { cn } from "@/lib/utils";

import { useWorkspace } from "../-workspace-context";
import { TitleBar } from "../../components/TitleBar";
import { useOnline } from "../../hooks/use-online";
import {
  SETTINGS_NAV,
  type SettingsNavSection,
  type SettingsPath,
  isWithinPane,
  settingsNavTrail,
} from "./settings/-nav";

// Marker carried on the error thrown by `beforeLoad` when offline. Compared by
// string (not identity) because `beforeLoad` and `errorComponent` can land in
// different build chunks under route code-splitting.
const OFFLINE_ERROR = "hearth:settings-offline";

export const Route = createFileRoute("/_workspace/settings")({
  head: () => ({ meta: [{ title: "Settings" }] }),
  // The settings panes are code-split and deliberately excluded from the
  // service-worker precache (see vite.config.ts): they read and toggle live
  // server state, so they're useless offline and shouldn't bloat the install.
  // The router fetches each matched route's component chunk during navigation,
  // so offline it would hit an uncached pane chunk and surface a chunk-load
  // error. Short-circuit here — a parent `beforeLoad` throw stops the router
  // before it loads any descendant pane — and render the offline notice via
  // `errorComponent` (which lives in this layout's own, still-precached chunk).
  beforeLoad: () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      throw new Error(OFFLINE_ERROR);
    }
  },
  component: SettingsLayout,
  errorComponent: SettingsErrorComponent,
});

function SettingsErrorComponent({ error }: ErrorComponentProps) {
  if (error instanceof Error && error.message === OFFLINE_ERROR) {
    return <SettingsOffline />;
  }
  // Not our sentinel — let the nearest ancestor boundary handle it, exactly as
  // it did before this route had an errorComponent.
  throw error;
}

// Carry the workspace search params (notably the side chat's `?chat=`) across
// navigation between settings panes, so the chat keeps running while the user
// moves around settings instead of being dropped on the first click.
const preserveSearch = (prev: Record<string, unknown>) => prev;

/** Ties the disclosure button to the menu it opens, for assistive tech. */
const NAV_ID = "settings-nav";

function SettingsLayout() {
  // Compact: a single column, and the nav collapses to one row — the pane
  // you're on sits in the title bar and the full menu drops in underneath when
  // you hit the caret (stacking the whole list open spent half a phone screen
  // on navigation). Otherwise the nav is a left sidebar. Driven by the shell's
  // container-aware `compact` flag so it tracks the header/dock rather than the
  // raw viewport (an open chat can collapse the layout at a wide window).
  const { compact } = useWorkspace();
  // Settings panes read and toggle live server state, and none of their
  // responses are runtime-cached by the service worker (only boot endpoints,
  // images, and wasm are — see vite.config.ts). Rendering them offline would
  // surface broken loads or stale toggles, so gate the whole section behind
  // connectivity rather than let a pane half-render.
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      className={cn(
        "flex flex-1 overflow-hidden",
        compact ? "flex-col" : "flex-row",
      )}
    >
      {compact ? (
        <>
          <TitleBar compactOnly>
            <SettingsMenuButton
              open={menuOpen}
              onToggle={() => setMenuOpen((open) => !open)}
            />
          </TitleBar>
          {/* Closing on select is what makes this a menu rather than a second
              layout mode. The handler runs on every pick, including the pane
              already active (which navigates nowhere), so the caret is never
              the only way back out. */}
          {menuOpen ? (
            <SettingsNav compact onNavigate={() => setMenuOpen(false)} />
          ) : null}
        </>
      ) : (
        <SettingsNav compact={false} />
      )}
      <div className="flex-1 overflow-y-auto">
        {online ? <Outlet /> : <SettingsOffline />}
      </div>
    </div>
  );
}

/**
 * The collapsed compact nav: the pane you're on, and the caret that reveals the
 * rest. Lives in the title bar, so closed it costs no rows at all.
 */
function SettingsMenuButton({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const { pathname } = useLocation();
  // The full trail, root included — "Settings › Schedules › Executed". A leaf
  // named on its own ("Executed") doesn't say what it's a history of, and this
  // row is the compact layout's only statement of where you are.
  const crumbs = ["Settings", ...settingsNavTrail(pathname)];
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={NAV_ID}
      className="flex min-w-0 items-center gap-1 rounded-md px-1.5 py-1 text-sm hover:bg-muted"
    >
      {crumbs.map((label, i) => (
        <Fragment key={label}>
          {i > 0 ? (
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : null}
          <span
            className={cn(
              "truncate",
              i === crumbs.length - 1 ? "font-medium" : "text-muted-foreground",
            )}
          >
            {label}
          </span>
        </Fragment>
      ))}
      <ChevronDown
        className={cn(
          "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
          open && "rotate-180",
        )}
      />
    </button>
  );
}

function SettingsOffline() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <WifiOff className="h-8 w-8 text-muted-foreground" />
      <div>
        <p className="text-sm font-medium">Settings aren’t available offline</p>
        <p className="mt-1 text-sm text-muted-foreground">
          These pages change settings on the server. Reconnect to view or update
          them.
        </p>
      </div>
    </div>
  );
}

function SettingsNav({
  compact,
  onNavigate,
}: {
  compact: boolean;
  onNavigate?: () => void;
}) {
  return (
    <nav
      id={NAV_ID}
      className={cn(
        "shrink-0 overflow-y-auto border-border px-2",
        compact ? "max-h-[60vh] w-full border-b py-3" : "w-60 border-r py-4",
      )}
    >
      <h2
        className={cn(
          "px-2 pb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
          compact ? "hidden" : "block",
        )}
      >
        Settings
      </h2>

      {SETTINGS_NAV.map((section) =>
        section.children ? (
          <NavGroup
            key={section.to}
            section={section}
            onNavigate={onNavigate}
          />
        ) : (
          <NavLink key={section.to} to={section.to} onNavigate={onNavigate}>
            {section.label}
          </NavLink>
        ),
      )}
    </nav>
  );
}

/** A single top-level settings link, styled like a NavGroup header. */
function NavLink({
  to,
  onNavigate,
  children,
}: {
  to: SettingsPath;
  onNavigate?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-1">
      <Link
        to={to}
        search={preserveSearch}
        onClick={onNavigate}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm"
        activeProps={{ className: "bg-muted font-medium text-foreground" }}
        inactiveProps={{
          className: "text-foreground/90 hover:bg-muted hover:text-foreground",
        }}
      >
        {children}
      </Link>
    </div>
  );
}

const leafClass = "flex items-center gap-2 rounded-md px-2 py-1 text-sm";
const leafActiveClass = "bg-muted font-medium text-foreground";
const leafInactiveClass =
  "text-muted-foreground hover:bg-muted hover:text-foreground";

/**
 * A collapsible nav group whose header navigates to the group's base pane. The
 * group is expanded whenever the active route is within it, so clicking the
 * header (which makes it active) expands the possibilities underneath — exactly
 * the "click Integrations → its services appear" behaviour.
 */
function NavGroup({
  section,
  onNavigate,
}: {
  section: SettingsNavSection;
  onNavigate?: () => void;
}) {
  const { to, label, children } = section;
  const { pathname } = useLocation();
  const within = isWithinPane(pathname, to);
  const exact = pathname === to;
  return (
    <div className="mb-1">
      <Link
        to={to}
        search={preserveSearch}
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm",
          exact
            ? "bg-muted font-medium text-foreground"
            : "text-foreground/90 hover:bg-muted hover:text-foreground",
        )}
      >
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            within && "rotate-90",
          )}
        />
        {label}
      </Link>
      {within ? (
        <ul className="mt-0.5 space-y-0.5 pl-5">
          {children?.map((leaf) => (
            <li key={leaf.to}>
              <Link
                to={leaf.to}
                search={preserveSearch}
                onClick={onNavigate}
                className={leafClass}
                activeProps={{ className: leafActiveClass }}
                inactiveProps={{ className: leafInactiveClass }}
              >
                {leaf.label}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
